import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SettingsBackupRestoreRoundedIcon from "@mui/icons-material/SettingsBackupRestoreRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  useEffect,
  useMemo,
  useState,
} from "react";

import { SoftDataGrid } from "../../../../components/data/SoftDataGrid";
import { BrandSpinner } from "../../../../components/feedback/BrandSpinner";

import { useAuthStore } from "../../../../stores/authStore";

import {
  getShops,
} from "../../../../services/shopsService";

import {
  assignFirewallShop,
  assignFirewallShops,
  createSophosFirewall,
  deleteSophosFirewall,
  forceDeleteSophosFirewall,
  getSophosFirewall,
  getSophosFirewalls,
  removeFirewallShop,
  restoreSophosFirewall,
  unassignFirewallShop,
  updateFirewallShopAssignment,
  updateSophosFirewall,
} from "../../../../services/sophosFirewallService";

import FirewallFormDialog from "../components/FirewallFormDialog";
import ShopAssignmentDialog from "../components/ShopAssignmentDialog";
import AssignmentEditDialog from "../components/AssignmentEditDialog";
import FirewallDetailsDrawer from "../components/FirewallDetailsDrawer";
import RecoveryDialog from "../components/RecoveryDialog";

import {
  formatDateTime,
  getApiError,
} from "../utils/firewallFormatters";

const fieldSx = {
  "& .MuiInputBase-root": {
    borderRadius: 0,
    fontSize: "0.72rem",
  },

  "& .MuiInputLabel-root": {
    fontSize: "0.72rem",
  },

  "& .MuiSelect-select": {
    fontSize: "0.72rem",
  },
};

const INITIAL_FILTERS = {
  search: "",
  shop_id: "",
  device_model: "",
  is_active: "",
};

function normaliseShopsResponse(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.shops?.data)) {
    return response.shops.data;
  }

  if (Array.isArray(response?.shops)) {
    return response.shops;
  }

  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
}

function extractPaginator(payload) {
  return (
    payload?.firewalls ??
    payload?.data?.firewalls ??
    payload?.data ??
    {}
  );
}

function extractRows(payload) {
  const paginator = extractPaginator(payload);

  if (Array.isArray(paginator?.data)) {
    return paginator.data;
  }

  return Array.isArray(paginator)
    ? paginator
    : [];
}

function getRoleName(user) {
  if (typeof user?.role === "string") {
    return user.role;
  }

  return (
    user?.role?.name ||
    user?.role_name ||
    ""
  );
}

function isSuperAdminUser(user) {
  return (
    String(getRoleName(user))
      .trim()
      .toLowerCase() === "super_admin"
  );
}

function FirewallStatusChip({ active }) {
  return (
    <Chip
      size="small"
      label={active ? "Active" : "Inactive"}
      sx={{
        borderRadius: 0,
        minWidth: 68,
        height: 24,
        fontSize: "0.66rem",
        fontWeight: 750,
        bgcolor: active
          ? "#ECFDF3"
          : "#FEF3F2",
        color: active
          ? "#027A48"
          : "#B42318",
      }}
    />
  );
}

function RowMenu({
  firewall,
  canManage,
  onView,
  onEdit,
  onAssign,
  onDelete,
}) {
  const [anchor, setAnchor] =
    useState(null);

  const close = () =>
    setAnchor(null);

  return (
    <>
      <IconButton
        size="small"
        onClick={(event) =>
          setAnchor(
            event.currentTarget,
          )
        }
      >
        <MoreVertRoundedIcon
          sx={{
            fontSize: 18,
          }}
        />
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        PaperProps={{
          sx: {
            borderRadius: 0,
            minWidth: 170,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            close();
            onView(firewall);
          }}
          sx={{
            fontSize: "0.72rem",
          }}
        >
          <VisibilityOutlinedIcon
            sx={{
              mr: 1,
              fontSize: 16,
            }}
          />

          View details
        </MenuItem>

        {canManage && (
          <MenuItem
            onClick={() => {
              close();
              onEdit(firewall);
            }}
            sx={{
              fontSize: "0.72rem",
            }}
          >
            <EditOutlinedIcon
              sx={{
                mr: 1,
                fontSize: 16,
              }}
            />

            Edit firewall
          </MenuItem>
        )}

        {canManage && (
          <MenuItem
            onClick={() => {
              close();
              onAssign(firewall);
            }}
            sx={{
              fontSize: "0.72rem",
            }}
          >
            <AddRoundedIcon
              sx={{
                mr: 1,
                fontSize: 16,
              }}
            />

            Assign shops
          </MenuItem>
        )}

        {canManage && (
          <MenuItem
            onClick={() => {
              close();
              onDelete(firewall);
            }}
            sx={{
              color: "error.main",
              fontSize: "0.72rem",
            }}
          >
            <DeleteOutlineRoundedIcon
              sx={{
                mr: 1,
                fontSize: 16,
              }}
            />

            Delete firewall
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

export default function FirewallConfigurationPage() {
  const client =
    useQueryClient();

  const user = useAuthStore(
    (state) => state.user,
  );

  const canManage =
    isSuperAdminUser(user);

  const [
    searchInput,
    setSearchInput,
  ] = useState("");

  const [
    filters,
    setFilters,
  ] = useState(
    INITIAL_FILTERS,
  );

  const [
    paginationModel,
    setPaginationModel,
  ] = useState({
    page: 0,
    pageSize: 20,
  });

  const [
    selectedFirewall,
    setSelectedFirewall,
  ] = useState(null);

  const [
    formMode,
    setFormMode,
  ] = useState("create");

  const [
    formOpen,
    setFormOpen,
  ] = useState(false);

  const [
    detailsOpen,
    setDetailsOpen,
  ] = useState(false);

  const [
    assignOpen,
    setAssignOpen,
  ] = useState(false);

  const [
    assignmentShop,
    setAssignmentShop,
  ] = useState(null);

  const [
    recoveryOpen,
    setRecoveryOpen,
  ] = useState(false);

  const [
    notice,
    setNotice,
  ] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((current) => ({
        ...current,
        search:
          searchInput.trim(),
      }));

      setPaginationModel(
        (current) => ({
          ...current,
          page: 0,
        }),
      );
    }, 450);

    return () =>
      clearTimeout(timer);
  }, [searchInput]);

  const showNotice = (
    message,
    severity = "success",
  ) => {
    setNotice({
      open: true,
      message:
        message ||
        "Operation completed.",
      severity,
    });
  };

  const closeNotice = () => {
    setNotice((current) => ({
      ...current,
      open: false,
    }));
  };

  const updateFilter = (
    name,
    value,
  ) => {
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));

    setPaginationModel(
      (current) => ({
        ...current,
        page: 0,
      }),
    );
  };

  const listQuery = useQuery({
    queryKey: [
      "sophos-firewalls",
      filters.search,
      filters.shop_id,
      filters.device_model,
      filters.is_active,
      paginationModel.page,
      paginationModel.pageSize,
    ],

    queryFn: () =>
      getSophosFirewalls({
        page:
          paginationModel.page + 1,

        per_page:
          paginationModel.pageSize,

        search:
          filters.search ||
          undefined,

        shop_id:
          filters.shop_id ||
          undefined,

        device_model:
          filters.device_model ||
          undefined,

        /*
         * Send actual booleans.
         * Do not send 1 or 0.
         */
        is_active:
          filters.is_active === ""
            ? undefined
            : filters.is_active,
      }),

    placeholderData:
      (previousData) =>
        previousData,
  });

const shopsQuery = useQuery({
  queryKey: [
    "firewall-authorised-shops",
  ],

  queryFn: async () => {
    const response =
      await getShops({
        page: 1,
        per_page: 100,

        /*
         * The shops endpoint expects 1/0
         * for query-string boolean filters.
         */
        is_active: 1,
      });

    return normaliseShopsResponse(
      response,
    );
  },

  staleTime:
    5 * 60 * 1000,
});

  const rows =
    extractRows(
      listQuery.data,
    );

  const paginator =
    extractPaginator(
      listQuery.data,
    );

  const shops =
    shopsQuery.data || [];

  const deviceModels =
    useMemo(() => {
      return [
        ...new Set(
          rows
            .map(
              (row) =>
                row.device_model,
            )
            .filter(Boolean),
        ),
      ].sort((a, b) =>
        String(a).localeCompare(
          String(b),
        ),
      );
    }, [rows]);

  const refreshAll =
    async () => {
      await client.invalidateQueries({
        queryKey: [
          "sophos-firewalls",
        ],
      });

      if (
        selectedFirewall?.id
      ) {
        try {
          const response =
            await getSophosFirewall(
              selectedFirewall.id,
            );

          setSelectedFirewall(
            response.firewall,
          );
        } catch {
          setSelectedFirewall(
            null,
          );

          setDetailsOpen(
            false,
          );
        }
      }
    };

  const createMutation =
    useMutation({
      mutationFn: (payload) =>
        createSophosFirewall({
          ...payload,

          /*
           * Guarantee Laravel receives
           * true or false.
           */
          is_active: Boolean(
            payload.is_active,
          ),
        }),

      onSuccess:
        async (response) => {
          setFormOpen(false);

          showNotice(
            response?.message ||
              "Sophos firewall created successfully.",
          );

          await refreshAll();
        },

      onError: (error) =>
        showNotice(
          getApiError(error),
          "error",
        ),
    });

  const updateMutation =
    useMutation({
      mutationFn: ({
        id,
        payload,
      }) =>
        updateSophosFirewall(
          id,
          {
            ...payload,

            ...(Object.prototype.hasOwnProperty.call(
              payload,
              "is_active",
            )
              ? {
                  is_active:
                    Boolean(
                      payload.is_active,
                    ),
                }
              : {}),
          },
        ),

      onSuccess:
        async (response) => {
          setFormOpen(false);

          setSelectedFirewall(
            response.firewall,
          );

          showNotice(
            response?.message ||
              "Sophos firewall updated successfully.",
          );

          await refreshAll();
        },

      onError: (error) =>
        showNotice(
          getApiError(error),
          "error",
        ),
    });

  const deleteMutation =
    useMutation({
      mutationFn:
        deleteSophosFirewall,

      onSuccess:
        async (response) => {
          setDetailsOpen(false);

          setSelectedFirewall(
            null,
          );

          showNotice(
            response?.message ||
              "Sophos firewall deleted successfully.",
          );

          await refreshAll();
        },

      onError: (error) =>
        showNotice(
          getApiError(error),
          "error",
        ),
    });

  const assignmentMutation =
    useMutation({
      mutationFn: ({
        id,
        payload,
      }) =>
        assignFirewallShops(
          id,
          {
            ...payload,

            is_primary:
              Boolean(
                payload.is_primary,
              ),
          },
        ),

      onSuccess:
        async (response) => {
          setAssignOpen(false);

          setSelectedFirewall(
            response.firewall,
          );

          showNotice(
            response?.message ||
              "Shops assigned successfully.",
          );

          await refreshAll();
        },

      onError: (error) =>
        showNotice(
          getApiError(error),
          "error",
        ),
    });

  const updateAssignmentMutation =
    useMutation({
      mutationFn: ({
        firewallId,
        shopId,
        payload,
      }) =>
        updateFirewallShopAssignment(
          firewallId,
          shopId,
          {
            ...payload,

            ...(Object.prototype.hasOwnProperty.call(
              payload,
              "is_active",
            )
              ? {
                  is_active:
                    Boolean(
                      payload.is_active,
                    ),
                }
              : {}),

            ...(Object.prototype.hasOwnProperty.call(
              payload,
              "is_primary",
            )
              ? {
                  is_primary:
                    Boolean(
                      payload.is_primary,
                    ),
                }
              : {}),
          },
        ),

      onSuccess:
        async (response) => {
          setAssignmentShop(
            null,
          );

          setSelectedFirewall(
            response.firewall,
          );

          showNotice(
            response?.message ||
              "Shop assignment updated successfully.",
          );

          await refreshAll();
        },

      onError: (error) =>
        showNotice(
          getApiError(error),
          "error",
        ),
    });

  const simpleMutation =
    useMutation({
      mutationFn: async ({
        action,
        firewallId,
        shopId,
      }) => {
        if (
          action === "unassign"
        ) {
          return unassignFirewallShop(
            firewallId,
            shopId,
          );
        }

        if (
          action === "remove"
        ) {
          return removeFirewallShop(
            firewallId,
            shopId,
          );
        }

        if (
          action === "restore"
        ) {
          return restoreSophosFirewall(
            firewallId,
          );
        }

        if (
          action === "force"
        ) {
          return forceDeleteSophosFirewall(
            firewallId,
          );
        }

        throw new Error(
          "Unsupported firewall action.",
        );
      },

      onSuccess:
        async (response) => {
          setRecoveryOpen(false);

          showNotice(
            response?.message ||
              "Operation completed successfully.",
          );

          await refreshAll();
        },

      onError: (error) =>
        showNotice(
          getApiError(error),
          "error",
        ),
    });

  const openDetails =
    async (firewall) => {
      try {
        const response =
          await getSophosFirewall(
            firewall.id,
          );

        setSelectedFirewall(
          response.firewall,
        );

        setDetailsOpen(true);
      } catch (error) {
        showNotice(
          getApiError(error),
          "error",
        );
      }
    };

  const startCreate = () => {
    setFormMode("create");
    setSelectedFirewall(null);
    setFormOpen(true);
  };

  const startEdit = (
    firewall,
  ) => {
    setSelectedFirewall(
      firewall,
    );

    setFormMode("edit");
    setFormOpen(true);
  };

  const openAssignments = (
    firewall,
  ) => {
    setSelectedFirewall(
      firewall,
    );

    setAssignOpen(true);
  };

  const confirmDelete = (
    firewall,
  ) => {
    const label =
      firewall.device_name ||
      firewall.device_serial_id;

    if (
      window.confirm(
        `Delete ${label}?`,
      )
    ) {
      deleteMutation.mutate(
        firewall.id,
      );
    }
  };

  const columns = [
    {
      field: "avatar",
      headerName: "",
      width: 52,
      sortable: false,
      filterable: false,

      renderCell: (params) => (
        <Avatar
          sx={{
            width: 28,
            height: 28,
            borderRadius: 0,
            bgcolor: "#EEF4FF",
            color: "#3538CD",
            fontSize: "0.62rem",
            fontWeight: 850,
          }}
        >
          {String(
            params.row.device_name ||
              params.row.device_serial_id ||
              "FW",
          )
            .slice(0, 2)
            .toUpperCase()}
        </Avatar>
      ),
    },

    {
      field: "device_name",
      headerName: "Firewall",
      minWidth: 220,
      flex: 1,

      renderCell: (params) => (
        <Box>
          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: 800,
              lineHeight: 1.25,
            }}
          >
            {params.row.device_name ||
              "Unnamed firewall"}
          </Typography>

          <Typography
            sx={{
              color: "#667085",
              fontSize: "0.64rem",
              mt: 0.2,
            }}
          >
            {
              params.row.device_serial_id
            }
          </Typography>
        </Box>
      ),
    },

    {
      field: "device_model",
      headerName: "Model / firmware",
      minWidth: 180,
      flex: 0.8,

      renderCell: (params) => (
        <Box>
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
            }}
          >
            {params.row.device_model ||
              "—"}
          </Typography>

          <Typography
            sx={{
              color: "#667085",
              fontSize: "0.63rem",
            }}
          >
            {params.row.firmware_version ||
              "No firmware"}
          </Typography>
        </Box>
      ),
    },

    {
      field: "network",
      headerName: "Network",
      minWidth: 180,
      flex: 0.8,

      renderCell: (params) => (
        <Box>
          <Typography
            sx={{
              fontSize: "0.7rem",
            }}
          >
            {params.row.last_public_ip ||
              "—"}
          </Typography>

          <Typography
            sx={{
              color: "#667085",
              fontSize: "0.63rem",
            }}
          >
            {params.row.location_name ||
              "No location"}
          </Typography>
        </Box>
      ),
    },

    {
      field: "shops_count",
      headerName: "Shop coverage",
      minWidth: 150,
      flex: 0.6,

      renderCell: (params) => (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
        >
          <Chip
            size="small"
            label={`${params.row.active_shops_count ?? 0} active`}
            sx={{
              borderRadius: 0,
              height: 23,
              fontSize: "0.63rem",
              bgcolor: "#EFF8FF",
              color: "#175CD3",
            }}
          />

          <Typography
            sx={{
              color: "#667085",
              fontSize: "0.63rem",
            }}
          >
            of{" "}
            {params.row.shops_count ??
              0}
          </Typography>
        </Stack>
      ),
    },

    {
      field: "last_seen_at",
      headerName: "Last seen",
      minWidth: 145,
      flex: 0.6,

      valueGetter: (_, row) =>
        formatDateTime(
          row.last_seen_at,
        ),
    },

    {
      field: "status",
      headerName: "Status",
      width: 95,

      renderCell: (params) => (
        <FirewallStatusChip
          active={Boolean(
            params.row.is_active,
          )}
        />
      ),
    },

    {
      field: "actions",
      headerName: "",
      width: 54,
      sortable: false,
      filterable: false,

      renderCell: (params) => (
        <RowMenu
          firewall={params.row}
          canManage={canManage}
          onView={openDetails}
          onEdit={startEdit}
          onAssign={
            openAssignments
          }
          onDelete={
            confirmDelete
          }
        />
      ),
    },
  ];

  return (
    <Box
      sx={{
        p: {
          xs: 1.2,
          md: 2,
        },
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 0,
        }}
      >
        <Stack
          direction={{
            xs: "column",
            lg: "row",
          }}
          spacing={1}
          alignItems={{
            xs: "stretch",
            lg: "center",
          }}
          sx={{
            p: 1.2,
            borderBottom:
              "1px solid #E3E7EC",
          }}
        >
          <Box
            sx={{
              flex: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.82rem",
                fontWeight: 850,
              }}
            >
              Firewall configuration
            </Typography>

            <Typography
              sx={{
                color: "#667085",
                fontSize: "0.66rem",
              }}
            >
              Manage Sophos appliances
              and shop assignments.
            </Typography>
          </Box>

          <TextField
            value={searchInput}
            onChange={(event) =>
              setSearchInput(
                event.target.value,
              )
            }
            placeholder="Search firewalls"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon
                    sx={{
                      fontSize: 17,
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              ...fieldSx,
              width: {
                xs: "100%",
                lg: 230,
              },
            }}
          />

          <FormControl
            size="small"
            sx={{
              width: {
                xs: "100%",
                lg: 190,
              },
              ...fieldSx,
            }}
          >
            <InputLabel>
              Shop
            </InputLabel>

            <Select
              value={
                filters.shop_id
              }
              label="Shop"
              onChange={(event) =>
                updateFilter(
                  "shop_id",
                  event.target.value,
                )
              }
            >
              <MenuItem value="">
                All visible shops
              </MenuItem>

              {shops.map((shop) => (
                <MenuItem
                  key={shop.id}
                  value={shop.id}
                  sx={{
                    fontSize: "0.72rem",
                  }}
                >
                  {shop.shop_name ||
                    shop.shop_code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={{
              width: {
                xs: "100%",
                lg: 160,
              },
              ...fieldSx,
            }}
          >
            <InputLabel>
              Model
            </InputLabel>

            <Select
              value={
                filters.device_model
              }
              label="Model"
              onChange={(event) =>
                updateFilter(
                  "device_model",
                  event.target.value,
                )
              }
            >
              <MenuItem value="">
                All models
              </MenuItem>

              {deviceModels.map(
                (model) => (
                  <MenuItem
                    key={model}
                    value={model}
                    sx={{
                      fontSize: "0.72rem",
                    }}
                  >
                    {model}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={{
              width: {
                xs: "100%",
                lg: 130,
              },
              ...fieldSx,
            }}
          >
            <InputLabel>
              Status
            </InputLabel>

            <Select
              value={
                filters.is_active
              }
              label="Status"
              onChange={(event) =>
                updateFilter(
                  "is_active",
                  event.target.value,
                )
              }
            >
              <MenuItem value="">
                All
              </MenuItem>

              <MenuItem value={true}>
                Active
              </MenuItem>

              <MenuItem value={false}>
                Inactive
              </MenuItem>
            </Select>
          </FormControl>

          <Tooltip title="Refresh">
            <span>
              <IconButton
                size="small"
                disabled={
                  listQuery.isFetching
                }
                onClick={() =>
                  listQuery.refetch()
                }
                sx={{
                  borderRadius: 0,
                  border:
                    "1px solid #D0D5DD",
                }}
              >
                <RefreshRoundedIcon
                  sx={{
                    fontSize: 18,
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>

          {canManage && (
            <Button
              variant="outlined"
              startIcon={
                <SettingsBackupRestoreRoundedIcon />
              }
              onClick={() =>
                setRecoveryOpen(true)
              }
              sx={{
                borderRadius: 0,
                whiteSpace: "nowrap",
                fontSize: "0.7rem",
              }}
            >
              Recovery
            </Button>
          )}

          {canManage && (
            <Button
              variant="contained"
              startIcon={
                <AddRoundedIcon />
              }
              onClick={
                startCreate
              }
              sx={{
                borderRadius: 0,
                whiteSpace: "nowrap",
                fontSize: "0.7rem",
              }}
            >
              Register firewall
            </Button>
          )}
        </Stack>

        {listQuery.isError && (
          <Alert
            severity="error"
            sx={{
              borderRadius: 0,
              fontSize: "0.7rem",
            }}
          >
            {getApiError(
              listQuery.error,
            )}
          </Alert>
        )}

        {shopsQuery.isError && (
          <Alert
            severity="warning"
            sx={{
              borderRadius: 0,
              fontSize: "0.7rem",
            }}
          >
            Shops could not be loaded.
          </Alert>
        )}

        {listQuery.isLoading ? (
          <Box
            sx={{
              minHeight: 300,
              display: "grid",
              placeItems: "center",
            }}
          >
            <BrandSpinner
              size={72}
            />
          </Box>
        ) : (
          <SoftDataGrid
            rows={rows}
            columns={columns}
            loading={
              listQuery.isFetching
            }
            rowCount={
              paginator?.total ??
              rows.length
            }
            paginationMode="server"
            paginationModel={
              paginationModel
            }
            onPaginationModelChange={
              setPaginationModel
            }
            pageSizeOptions={[
              10,
              20,
              50,
              100,
            ]}
            getRowId={(row) =>
              row.id
            }
          />
        )}
      </Paper>

      <FirewallFormDialog
        open={formOpen}
        mode={formMode}
        firewall={
          selectedFirewall
        }
        shops={shops}
        loading={
          createMutation.isPending ||
          updateMutation.isPending
        }
        onClose={() =>
          setFormOpen(false)
        }
        onSubmit={(payload) => {
          if (
            formMode === "create"
          ) {
            createMutation.mutate(
              payload,
            );

            return;
          }

          if (
            !selectedFirewall?.id
          ) {
            showNotice(
              "No firewall selected.",
              "error",
            );

            return;
          }

          updateMutation.mutate({
            id:
              selectedFirewall.id,
            payload,
          });
        }}
      />

      <FirewallDetailsDrawer
        open={detailsOpen}
        firewall={
          selectedFirewall
        }
        canManage={canManage}
        onClose={() =>
          setDetailsOpen(false)
        }
        onEdit={() => {
          setDetailsOpen(false);

          startEdit(
            selectedFirewall,
          );
        }}
        onAssign={() =>
          setAssignOpen(true)
        }
        onEditAssignment={(
          shop,
        ) =>
          setAssignmentShop(
            shop,
          )
        }
        onUnassign={(shop) => {
          if (
            window.confirm(
              `Unassign ${shop.shop_name}?`,
            )
          ) {
            simpleMutation.mutate({
              action:
                "unassign",

              firewallId:
                selectedFirewall.id,

              shopId:
                shop.id,
            });
          }
        }}
        onRemoveAssignment={(
          shop,
        ) => {
          if (
            window.confirm(
              `Permanently remove ${shop.shop_name}?`,
            )
          ) {
            simpleMutation.mutate({
              action:
                "remove",

              firewallId:
                selectedFirewall.id,

              shopId:
                shop.id,
            });
          }
        }}
      />

      <ShopAssignmentDialog
        open={assignOpen}
        firewall={
          selectedFirewall
        }
        shops={shops}
        loading={
          assignmentMutation.isPending
        }
        onClose={() =>
          setAssignOpen(false)
        }
        onSubmit={(payload) => {
          if (
            !selectedFirewall?.id
          ) {
            return;
          }

          assignmentMutation.mutate({
            id:
              selectedFirewall.id,
            payload,
          });
        }}
      />

      <AssignmentEditDialog
        open={Boolean(
          assignmentShop,
        )}
        shop={assignmentShop}
        loading={
          updateAssignmentMutation.isPending
        }
        onClose={() =>
          setAssignmentShop(null)
        }
        onSubmit={(payload) => {
          if (
            !selectedFirewall?.id ||
            !assignmentShop?.id
          ) {
            return;
          }

          updateAssignmentMutation.mutate({
            firewallId:
              selectedFirewall.id,

            shopId:
              assignmentShop.id,

            payload,
          });
        }}
      />

      <RecoveryDialog
        open={recoveryOpen}
        loading={
          simpleMutation.isPending
        }
        onClose={() =>
          setRecoveryOpen(false)
        }
        onRestore={(id) =>
          simpleMutation.mutate({
            action: "restore",
            firewallId: id,
          })
        }
        onForceDelete={(id) => {
          if (
            window.confirm(
              "This permanently deletes the firewall. Continue?",
            )
          ) {
            simpleMutation.mutate({
              action: "force",
              firewallId: id,
            });
          }
        }}
      />

      <Snackbar
        open={notice.open}
        autoHideDuration={5000}
        onClose={closeNotice}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          severity={
            notice.severity
          }
          onClose={closeNotice}
          variant="filled"
          sx={{
            borderRadius: 0,
            fontSize: "0.72rem",
          }}
        >
          {notice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}